// src/Pages/Admin/cupones/CuponesAdminPage.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  doc,
  where,                // ✅
  getCountFromServer,   // ✅
} from "firebase/firestore";
import { db } from "../../../firebase/config";
import {
  Plus,
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Trash2,
  Pencil,
  CalendarDays,
  Percent,
  Hash,
  RefreshCw,
} from "lucide-react";
import Select from "react-select";
import { createPortal } from "react-dom";


const clp = (n) =>
  typeof n === "number" ? `$${n.toLocaleString("es-CL")}` : "—";

// ===== Helpers normalización (para comparar estable) =====
const normKey = (s = "") =>
  s
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

// ===== Flatten variantes por servicio (array u objeto planes) =====
const getVariantesFlat = (serv) => {
  const v = serv?.variantes;
  const out = [];
  if (!v) return out;

  // Caso array (normal)
  if (Array.isArray(v)) {
    v.forEach((it) => {
      const nombre = it?.nombre || it?.title || it?.id;
      if (!nombre) return;

      out.push({
        servicioId: serv.id,
        servicioTitle: serv.title || serv.id,
        varianteKey: it?.id || nombre, // preferir id si existe
        varianteNombre: nombre,
      });
    });
    return out;
  }

  // Caso objeto (planes por nivel)
  if (typeof v === "object") {
    Object.values(v).forEach((lista) => {
      (lista || []).forEach((p) => {
        const nombre = p?.nombre || p?.title || p?.id;
        if (!nombre) return;

        out.push({
          servicioId: serv.id,
          servicioTitle: serv.title || serv.id,
          varianteKey: p?.id || nombre,
          varianteNombre: nombre,
        });
      });
    });
  }

  return out;
};


const emptyForm = {
  codigo: "",
  tipo: "porcentaje",
  valor: "",
  activo: true,
  expira: "",
  usosMax: "",
};

const CuponesAdminPage = () => {
  const [cupones, setCupones] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [filtroActivo, setFiltroActivo] = useState("todos"); // todos | activos | inactivos

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Servicios para limitar el cupón
  const [servicios, setServicios] = useState([]);
  const [aplicacion, setAplicacion] = useState("todos"); // "todos" | "algunos" | "uno"
  const [serviciosPermitidos, setServiciosPermitidos] = useState([]);
  // Variantes
  const [aplicacionVariantes, setAplicacionVariantes] = useState("todas"); // "todas" | "algunas"
  const [variantesPermitidas, setVariantesPermitidas] = useState([]); // tokens "servicioId::varianteKeyNorm"

  const [usosCalcById, setUsosCalcById] = useState({}); // { [cuponId]: count }
  const [syncingUsos, setSyncingUsos] = useState(false);

  // ======== Cargar cupones en tiempo real ========
  useEffect(() => {
    const qCupones = query(collection(db, "cupones"), orderBy("codigo", "asc"));
    const unsub = onSnapshot(qCupones, (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setCupones(list);
      setLoading(false);

      // ✅ recalcular usos reales desde reservas
      recalcularUsosDesdeReservas(list);
    });
    return () => unsub();
  }, []);

  // ======== Cargar servicios disponibles ========
  useEffect(() => {
    const qServicios = query(
      collection(db, "editar_servicios"),
      orderBy("orden", "asc")
    );
    const unsub = onSnapshot(qServicios, (snap) => {
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setServicios(lista);
    });
    return () => unsub();
  }, []);

const usosReqRef = useRef(0);

// intenta contar en reservas probando distintos campos donde podría estar el código
const countCuponUsos = async (codigoUpper) => {
  const paths = [
    "cupon_codigo",        // ✅ TU CAMPO REAL (según Firestore)
    "cupon.codigo",        // por si algún doc antiguo lo guardó como map
    "cuponCodigo",
    "cupon",               // por si quedó como string
  ];

  for (const fieldPath of paths) {
    try {
      const q = query(
        collection(db, "reservas"),
        where(fieldPath, "==", codigoUpper)
      );

      const agg = await getCountFromServer(q);
      const count = agg.data().count || 0;

      if (count > 0) return count;
    } catch (e) {
      // si falla (índice / tipo / no consultable), prueba el siguiente
    }
  }

  return 0;
};


const recalcularUsosDesdeReservas = async (listaCupones) => {
  const reqId = ++usosReqRef.current;
  setSyncingUsos(true);

  try {
    const entries = await Promise.all(
      (listaCupones || []).map(async (c) => {
        const codigoUpper = (c.codigo || "").toUpperCase().trim();
        const count = codigoUpper ? await countCuponUsos(codigoUpper) : 0;
        return [c.id, count];
      })
    );

    // evita “pisadas” si entran respuestas viejas
    if (reqId !== usosReqRef.current) return;

    setUsosCalcById(Object.fromEntries(entries));
  } catch (e) {
    console.error("[Cupones] Error recalculando usos:", e);
  } finally {
    if (reqId === usosReqRef.current) setSyncingUsos(false);
  }
};


  // ======== Derivados: filtrado + stats ========
  const hoy = new Date().toISOString().slice(0, 10);

  const cuponesFiltrados = useMemo(() => {
    return cupones
      .filter((c) => {
        if (filtroActivo === "activos" && !c.activo) return false;
        if (filtroActivo === "inactivos" && c.activo) return false;
        if (
          search &&
          !c.codigo.toLowerCase().includes(search.trim().toLowerCase())
        )
          return false;
        return true;
      })
      .sort((a, b) => a.codigo.localeCompare(b.codigo));
  }, [cupones, filtroActivo, search]);

  const stats = useMemo(() => {
    const total = cupones.length;
    const activos = cupones.filter((c) => c.activo).length;
    const expirados = cupones.filter((c) => c.expira && c.expira < hoy).length;

    const conUso = cupones.filter((c) => {
      const usosReal = usosCalcById[c.id] ?? (c.usos || 0);
      return usosReal > 0;
    }).length;

    return { total, activos, expirados, conUso };
  }, [cupones, hoy, usosCalcById]);


  const opcionesVariantes = useMemo(() => {
  if (!servicios || servicios.length === 0) return [];

  const idsPermitidos =
    aplicacion === "todos"
      ? servicios.map((s) => s.id)
      : (serviciosPermitidos || []);

  const flat = servicios
    .filter((s) => idsPermitidos.includes(s.id))
    .flatMap((s) => getVariantesFlat(s));

  // agrupar por servicio
  const grupos = new Map();
  flat.forEach((x) => {
    if (!grupos.has(x.servicioId)) {
      grupos.set(x.servicioId, { label: x.servicioTitle, options: [] });
    }
    grupos.get(x.servicioId).options.push({
      value: `${x.servicioId}::${normKey(x.varianteKey)}`,
      label: x.varianteNombre,
    });
  });

  return Array.from(grupos.values()).filter((g) => g.options.length > 0);
}, [servicios, aplicacion, serviciosPermitidos]);


  // ======== Helpers formulario ========
  const openCreateModal = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      expira: hoy,
      usosMax: 100,
    });
    setAplicacion("todos");
    setServiciosPermitidos([]);
    setAplicacionVariantes("todas");
    setVariantesPermitidas([]);
    setModalOpen(true);
  };

  const openEditModal = (cup) => {
    setEditingId(cup.id);
    setForm({
      codigo: cup.codigo || "",
      tipo: cup.tipo || "porcentaje",
      valor: cup.valor ?? "",
      activo: !!cup.activo,
      expira: cup.expira || hoy,
      usosMax: cup.usosMax ?? "",
    });
    setAplicacion(cup.aplicacion || "todos");
    setServiciosPermitidos(cup.serviciosPermitidos || []);
    setAplicacionVariantes(cup.aplicacionVariantes || "todas");
    setVariantesPermitidas(cup.variantesPermitidas || []);
    setModalOpen(true);
  };

    const closeModal = () => {
      if (saving) return;
      setModalOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      setAplicacion("todos");
      setServiciosPermitidos([]);
      setAplicacionVariantes("todas");
      setVariantesPermitidas([]);
    };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : name === "codigo"
          ? value.toUpperCase()
          : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo.trim()) {
      alert("El código del cupón es obligatorio.");
      return;
    }
    if (!form.expira) {
      alert("Debes definir una fecha de expiración.");
      return;
    }
    const valorNum = Number(form.valor);
    const usosMaxNum = Number(form.usosMax);
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      alert("El valor del cupón debe ser un número mayor a 0.");
      return;
    }
    if (!Number.isFinite(usosMaxNum) || usosMaxNum <= 0) {
      alert("El máximo de usos debe ser un número mayor a 0.");
      return;
    }

    if (aplicacionVariantes === "algunas" && variantesPermitidas.length === 0) {
      alert("Si limitas por variantes, debes seleccionar al menos una variante.");
      return;
    }

    const payload = {
      codigo: form.codigo.trim().toUpperCase(),
      tipo: form.tipo,
      valor: valorNum,
      activo: !!form.activo,
      expira: form.expira,
      usosMax: usosMaxNum,
      aplicacion,
      serviciosPermitidos,

      // ✅ NUEVO
      aplicacionVariantes,
      variantesPermitidas,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, "cupones", editingId), payload);
      } else {
        await addDoc(collection(db, "cupones"), {
          ...payload,
          usos: 0,
          creadoEn: serverTimestamp(),
        });
      }
      closeModal();
    } catch (err) {
      console.error(err);
      alert("Error al guardar el cupón.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (cup) => {
    try {
      await updateDoc(doc(db, "cupones", cup.id), {
        activo: !cup.activo,
      });
    } catch (err) {
      console.error(err);
      alert("No se pudo actualizar el estado del cupón.");
    }
  };

  const resetUsos = async (cup) => {
    if (!window.confirm(`¿Reiniciar usos del cupón ${cup.codigo}?`)) return;
    try {
      await updateDoc(doc(db, "cupones", cup.id), { usos: 0 });
    } catch (err) {
      console.error(err);
      alert("No se pudo reiniciar los usos.");
    }
  };

  const handleDelete = async (cup) => {
    if (
      !window.confirm(
        `¿Eliminar definitivamente el cupón ${cup.codigo}? Esta acción no se puede deshacer.`
      )
    )
      return;
    try {
      await deleteDoc(doc(db, "cupones", cup.id));
    } catch (err) {
      console.error(err);
      alert("No se pudo eliminar el cupón.");
    }
  };

  // ======== Render ========
  return (
    <div className="px-6 py-6 space-y-6">
      {/* Título + acciones */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cupones de descuento</h1>
          <p className="text-sm text-slate-600">Administra los códigos de descuento que se pueden usar en la plataforma de reservas.</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-2.5 text-sm font-semibold shadow-md hover:bg-blue-700 transition"
        >
          <Plus size={16} />
          Nuevo cupón
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <div className="text-xs font-medium text-slate-500">Total</div>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900">
              {stats.total}
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 shadow-sm">
          <div className="text-xs font-medium text-emerald-700">Activos</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-2xl font-bold text-emerald-800">
              {stats.activos}
            </span>
            <CheckCircle2 className="text-emerald-500" size={18} />
          </div>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 shadow-sm">
          <div className="text-xs font-medium text-amber-800">Expirados</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-2xl font-bold text-amber-900">
              {stats.expirados}
            </span>
            <CalendarDays className="text-amber-600" size={18} />
          </div>
        </div>
        <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-4 shadow-sm">
          <div className="text-xs font-medium text-indigo-800">
            Usados al menos una vez
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-2xl font-bold text-indigo-900">
              {stats.conUso}
            </span>
            <Hash className="text-indigo-600" size={18} />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div className="flex-1 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-3 top-2.5 text-slate-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Buscar por código (ej: EKINE10)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-slate-400" />
            <select
              isSearchable={false}
              value={filtroActivo}
              onChange={(e) => setFiltroActivo(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
            >
              <option value="todos">Todos</option>
              <option value="activos">Solo activos</option>
              <option value="inactivos">Solo inactivos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Listado de cupones: tabla en desktop, tarjetas en mobile */}

      {/* DESKTOP: tabla */}
      <div className="hidden md:block">
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Código</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-left">Valor</th>
                  <th className="px-4 py-3 text-left">Vigencia</th>
                  <th className="px-4 py-3 text-left">Usos</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      Cargando cupones...
                    </td>
                  </tr>
                )}

                {!loading && cuponesFiltrados.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No hay cupones que coincidan con el filtro.
                    </td>
                  </tr>
                )}

                {!loading &&
                  cuponesFiltrados.map((c) => {
                    const expirado = c.expira && c.expira < hoy;
                    const usos = usosCalcById[c.id] ?? (c.usos || 0);
                    const usosMax = c.usosMax ?? "—";

                    return (
                      <tr
                        key={c.id}
                        className="border-t border-slate-100 hover:bg-slate-50/70 transition"
                      >
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          {c.codigo}
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px]">
                            <Percent size={12} />
                            {c.tipo === "porcentaje"
                              ? "Porcentaje"
                              : "Monto fijo"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {c.tipo === "porcentaje"
                            ? `${c.valor}%`
                            : clp(c.valor)}
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="flex flex-col">
                            <span
                              className={
                                expirado
                                  ? "text-red-600 font-medium"
                                  : "text-slate-800"
                              }
                            >
                              {c.expira || "—"}
                            </span>
                            {expirado && (
                              <span className="text-[11px] text-red-500">
                                Expirado
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">
                              {usos}/{usosMax}
                            </span>
                            {usos > 0 && (
                              <button
                                onClick={() => resetUsos(c)}
                                className="inline-flex items-center rounded-full border border-slate-200 p-1 hover:bg-slate-100"
                                title="Reiniciar usos"
                              >
                                <RefreshCw
                                  size={12}
                                  className="text-slate-500"
                                />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleActivo(c)}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold border ${
                              c.activo
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-slate-100 text-slate-500 border-slate-200"
                            }`}
                          >
                            {c.activo ? (
                              <>
                                <CheckCircle2 size={12} />
                                Activo
                              </>
                            ) : (
                              <>
                                <XCircle size={12} />
                                Inactivo
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <button
                              onClick={() => openEditModal(c)}
                              className="inline-flex items-center rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                            >
                              <Pencil size={12} className="mr-1" />
                              Editar
                            </button>
                            <button
                              onClick={() => handleDelete(c)}
                              className="inline-flex items-center rounded-lg border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={12} className="mr-1" />
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MOBILE: tarjetas */}
      <div className="space-y-3 md:hidden">
        {loading && (
          <div className="rounded-2xl border border-slate-100 bg-white px-4 py-4 text-center text-sm text-slate-400">
            Cargando cupones...
          </div>
        )}

        {!loading && cuponesFiltrados.length === 0 && (
          <div className="rounded-2xl border border-slate-100 bg-white px-4 py-4 text-center text-sm text-slate-400">
            No hay cupones que coincidan con el filtro.
          </div>
        )}

        {!loading &&
          cuponesFiltrados.map((c) => {
            const expirado = c.expira && c.expira < hoy;
            const usos = usosCalcById[c.id] ?? (c.usos || 0);
            const usosMax = c.usosMax ?? "—";

            return (
              <div
                key={c.id}
                className="bg-white border border-slate-100 rounded-2xl px-4 py-3 shadow-sm flex flex-col gap-2"
              >
                {/* primera fila: código + estado */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {c.codigo}
                    </div>
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-700">
                      <Percent size={12} />
                      {c.tipo === "porcentaje"
                        ? `${c.valor}% descuento`
                        : `${clp(c.valor)} descuento`}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleActivo(c)}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold border ${
                      c.activo
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}
                  >
                    {c.activo ? (
                      <>
                        <CheckCircle2 size={12} />
                        Activo
                      </>
                    ) : (
                      <>
                        <XCircle size={12} />
                        Inactivo
                      </>
                    )}
                  </button>
                </div>

                {/* segunda fila: vigencia + usos */}
                <div className="text-[11px] text-slate-600 flex flex-wrap gap-y-1">
                  <div className="w-1/2">
                    <span className="font-semibold">Vigencia: </span>
                    <span className={expirado ? "text-red-600" : ""}>
                      {c.expira || "—"}
                    </span>
                    {expirado && (
                      <span className="block text-[11px] text-red-500">
                        Expirado
                      </span>
                    )}
                  </div>
                  <div className="w-1/2">
                    <span className="font-semibold">Usos: </span>
                    {usos}/{usosMax}
                    {usos > 0 && (
                      <button
                        onClick={() => resetUsos(c)}
                        className="ml-2 inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 hover:bg-slate-100"
                      >
                        <RefreshCw size={11} className="text-slate-500" />
                      </button>
                    )}
                  </div>
                </div>

                {/* acciones */}
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={() => openEditModal(c)}
                    className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1 text-[11px] text-slate-700 hover:bg-slate-100"
                  >
                    <Pencil size={11} className="mr-1" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(c)}
                    className="inline-flex items-center rounded-lg border border-red-200 px-3 py-1 text-[11px] text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={11} className="mr-1" />
                    Eliminar
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* Modal crear / editar */}
      {modalOpen && 
      createPortal(
          <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm overflow-y-auto">
            <div className="min-h-[100svh] w-full flex items-start justify-center p-4 sm:p-6 pt-24 sm:pt-28">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[calc(100svh-2rem)] overflow-y-auto p-4 sm:p-6 relative">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    {editingId ? "Editar cupón" : "Nuevo cupón"}
                  </h2>
                  <p className="text-xs text-slate-500">
                    Define el código, tipo de descuento y vigencia.
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-slate-400 hover:text-slate-600 text-sm"
                >
                  Cerrar
                </button>
              </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Código
                  </label>
                  <input
                    name="codigo"
                    value={form.codigo}
                    onChange={handleChange}
                    placeholder="EKINE10"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Se mostrará tal cual al paciente.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Tipo de descuento
                  </label>
                  <select
                    isSearchable={false}
                    name="tipo"
                    value={form.tipo}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  >
                    <option value="porcentaje">Porcentaje (%)</option>
                    <option value="monto">Monto fijo ($)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Valor
                  </label>
                  <input
                    name="valor"
                    type="number"
                    min={1}
                    value={form.valor}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Si es porcentaje, 10 = 10%.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Usos máximos
                  </label>
                  <input
                    name="usosMax"
                    type="number"
                    min={1}
                    value={form.usosMax}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Fecha de expiración
                  </label>
                  <input
                    name="expira"
                    type="date"
                    value={form.expira}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* NUEVO BLOQUE: APLICACIÓN DEL CUPÓN */}
              <div className="mt-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Aplicar cupón a
                </label>
                <select
                  isSearchable={false}
                  value={aplicacion}
                  onChange={(e) => {
                    setAplicacion(e.target.value);
                    setServiciosPermitidos([]);
                    setAplicacionVariantes("todas");
                    setVariantesPermitidas([]);
                  }}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40"
                >
                  <option value="todos">Todos los servicios</option>
                  <option value="algunos">Algunos servicios</option>
                  <option value="uno">Un solo servicio</option>
                </select>
              </div>

              {aplicacion === "algunos" && (
                <div className="mt-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Selecciona los servicios permitidos
                  </label>
                  <Select
                    isMulti
                    isSearchable={false}
                    options={servicios.map((s) => ({
                      value: s.id,
                      label: s.title || s.id,   // fallback al id si no tiene title
                    }))}
                    value={servicios
                      .filter((s) => serviciosPermitidos.includes(s.id))
                      .map((s) => ({ value: s.id, label: s.title || s.id }))}
                      onChange={(vals) => {
                        setServiciosPermitidos(vals.map((v) => v.value));
                        setAplicacionVariantes("todas");
                        setVariantesPermitidas([]);
                      }}
                    className="text-sm"
                  />
                </div>
              )}

              {aplicacion === "uno" && (
                <div className="mt-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Selecciona el servicio permitido
                  </label>
                  <Select
                  isSearchable={false}
                  options={servicios.map((s) => ({
                    value: s.id,
                    label: s.title || s.id,   // fallback al id si no tiene title
                  }))}
                    value={
                      serviciosPermitidos.length === 1
                        ? {
                            value: serviciosPermitidos[0],
                            label:
                          servicios.find((s) => s.id === serviciosPermitidos[0])?.title
                            ?? "Servicio",
                          }
                        : null
                    }
                    onChange={(v) => {
                      setServiciosPermitidos(v ? [v.value] : []);
                      setAplicacionVariantes("todas");
                      setVariantesPermitidas([]);
                    }}
                    className="text-sm"
                  />
                </div>
              )}

              {/* BLOQUE: LIMITAR POR VARIANTES */}
              <div className="mt-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Limitar a variantes
                </label>
                <select
                  isSearchable={false}
                  value={aplicacionVariantes}
                  onChange={(e) => {
                    setAplicacionVariantes(e.target.value);
                    setVariantesPermitidas([]);
                  }}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/40"
                >
                  <option value="todas">
                    Todas las variantes (del/los servicio(s) permitido(s))
                  </option>
                  <option value="algunas">Solo algunas variantes</option>
                </select>
              </div>

              {aplicacionVariantes === "algunas" && (
                <div className="mt-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Selecciona variantes permitidas
                  </label>

                  <Select
                    isMulti
                    isSearchable={false}
                    options={opcionesVariantes} // 👈 opciones agrupadas por servicio
                    value={(() => {
                      const map = new Map();
                      (opcionesVariantes || []).forEach((g) =>
                        (g.options || []).forEach((o) => map.set(o.value, o))
                      );
                      return (variantesPermitidas || [])
                        .map((v) => map.get(v))
                        .filter(Boolean);
                    })()}
                    onChange={(vals) => setVariantesPermitidas((vals || []).map((v) => v.value))}
                    className="text-sm"
                  />

                  <p className="mt-1 text-[11px] text-slate-400">
                    El cupón solo funcionará si el paciente elige exactamente una de estas variantes.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                <input
                  id="activo"
                  name="activo"
                  type="checkbox"
                  checked={form.activo}
                  onChange={handleChange}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="activo"
                  className="text-xs text-slate-700 select-none"
                >
                  Cupón activo y disponible para usar
                </label>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-800"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-2 text-xs font-semibold shadow hover:bg-blue-700 disabled:opacity-60"
                >
                  {saving
                    ? "Guardando..."
                    : editingId
                    ? "Guardar cambios"
                    : "Crear cupón"}
                  </button>
                  </div>
                </form>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CuponesAdminPage;
